const isPhone = () => window.matchMedia('(max-width: 920px)').matches
const isSmallPhone = () => window.matchMedia('(max-width: 600px)').matches
const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
const randomInRange = (min, max) => Math.random() * (max - min) + min
const easeOutCubic = t => 1 - (1 - t) ** 3
let stableViewportCache = { key: '', height: 0 }

const getStableViewportHeight = () => {
	const viewportWidth =
		window.innerWidth || document.documentElement.clientWidth || 0
	const screenWidth = window.screen?.width ?? viewportWidth
	const screenHeight = window.screen?.height ?? 0
	const key = `${screenWidth}x${screenHeight}:${viewportWidth}`

	if (stableViewportCache.key === key && stableViewportCache.height) {
		return stableViewportCache.height
	}

	const measureElem = document.createElement('div')
	measureElem.style.cssText = [
		'position: fixed',
		'left: 0',
		'top: 0',
		'width: 0',
		'height: 100lvh',
		'visibility: hidden',
		'pointer-events: none',
	].join(';')
	document.body.appendChild(measureElem)

	const measuredHeight = measureElem.getBoundingClientRect().height
	measureElem.remove()

	stableViewportCache = {
		key,
		height: Math.max(
			measuredHeight,
			window.innerHeight || 0,
			window.visualViewport?.height || 0,
			document.documentElement.clientHeight || 0,
		),
	}

	return stableViewportCache.height
}

const escapeHTML = value => {
	const elem = document.createElement('div')
	elem.textContent = value
	return elem.innerHTML
}

const initLoadingType = loadingElem => {
	if (!loadingElem || !window.TypeIt) {
		return () => {}
	}

	const destroyers = []
	let startDelay = 0

	loadingElem.querySelectorAll('[data-typeit] > div').forEach(typeitElem => {
		const fullText = typeitElem.textContent
		const loopElem = typeitElem?.querySelector('[data-loop]')
		const loopText = loopElem?.textContent
		const loopTagName = loopElem?.tagName.toLowerCase()
		const nextLoopElem = loopTagName
			? document.createElement(loopTagName)
			: null
		const staticText =
			loopElem && loopText
				? fullText.slice(0, fullText.lastIndexOf(loopText))
				: fullText
		let loopInstance = null

		typeitElem.textContent = ''

		const instance = new window.TypeIt(typeitElem, {
			speed: 16,
			cursor: false,
			startDelay,
			waitUntilVisible: true,
			afterComplete: () => {
				if (!loopElem || !nextLoopElem || loopInstance) {
					return
				}

				nextLoopElem.dataset.loop = ''
				typeitElem.appendChild(nextLoopElem)
				loopInstance = new window.TypeIt(nextLoopElem, {
					speed: 200,
					deleteSpeed: 0,
					cursor: false,
					loop: true,
					loopDelay: 0,
					startDelay: 0,
					waitUntilVisible: true,
				})
					.type(escapeHTML(loopText))
					.go()
			},
		})
			.type(escapeHTML(staticText))
			.go()

		destroyers.push(() => {
			loopInstance?.destroy()
			instance.destroy()
		})

		startDelay += 300
	})

	loadingElem.classList.add('ready')

	return () => {
		destroyers.forEach(destroy => destroy())
	}
}

const getItemMetrics = itemElems =>
	itemElems.map(item => {
		const { width, height } = item.getBoundingClientRect()

		return {
			width,
			height,
			area: width * height,
		}
	})

const getItemsCanvasSize = (stageElem, itemsElem, itemElems) => {
	const phone = isPhone()
	const stageRect = stageElem.getBoundingClientRect()
	const metrics = getItemMetrics(itemElems)
	const totalArea = metrics.reduce((sum, item) => sum + item.area, 0)
	const maxItemWidth = Math.max(...metrics.map(item => item.width), 0)
	const maxItemHeight = Math.max(...metrics.map(item => item.height), 0)
	const viewportWidth = stageRect.width || window.innerWidth
	const viewportHeight = phone
		? getStableViewportHeight()
		: stageRect.height || window.innerHeight
	const density = isSmallPhone() ? 0.435 : phone ? 0.415 : 0.155

	if (!totalArea || !viewportWidth || !viewportHeight) {
		return {
			width: viewportWidth,
			height: viewportHeight,
		}
	}

	if (phone) {
		const { width } = itemsElem.getBoundingClientRect()
		const canvasWidth = width || viewportWidth
		const densityHeight = totalArea / (canvasWidth * density)
		const minHeight = Math.max(viewportHeight, maxItemHeight * 2)

		return {
			width: canvasWidth,
			height: Math.ceil(Math.max(densityHeight, minHeight)),
		}
	}

	const aspectRatio = viewportWidth / viewportHeight
	const densityArea = totalArea / density
	const densityWidth = Math.sqrt(densityArea * aspectRatio)
	const densityHeight = densityWidth / aspectRatio
	const minWidth = Math.max(viewportWidth, maxItemWidth * 2)
	const minHeight = Math.max(viewportHeight, maxItemHeight * 2)

	return {
		width: Math.ceil(Math.max(densityWidth, minWidth)),
		height: Math.ceil(Math.max(densityHeight, minHeight)),
	}
}

const setItemsCanvasSize = (stageElem, itemsElem, itemElems) => {
	const phone = isPhone()

	if (phone) {
		itemsElem.style.removeProperty('width')
	}

	const { width, height } = getItemsCanvasSize(
		stageElem,
		itemsElem,
		itemElems,
	)

	if (!phone) {
		itemsElem.style.width = `${width}px`
	}

	itemsElem.style.height = `${height}px`
}

const scrollStageToCenter = (stageElem, itemsElem) => {
	if (isPhone()) {
		return
	}

	const stageRect = stageElem.getBoundingClientRect()
	const itemsRect = itemsElem.getBoundingClientRect()
	const scrollLeft = (itemsRect.width - stageRect.width) / 2
	const scrollTop = (itemsRect.height - stageRect.height) / 2
	stageElem.scrollTo({ top: scrollTop, left: scrollLeft })
}

const getPlacementBounds = (
	item,
	width,
	height,
	paddingRatio,
	overhangRatio = 0,
) => {
	const paddingX = width * paddingRatio
	const paddingY = height * paddingRatio
	const halfWidth = item.width / 2
	const halfHeight = item.height / 2
	const overhangX = item.width * overhangRatio
	const overhangY = item.height * overhangRatio
	const hardMinX = halfWidth - overhangX
	const hardMaxX = Math.max(hardMinX, width - halfWidth + overhangX)
	const hardMinY = halfHeight - overhangY
	const hardMaxY = Math.max(hardMinY, height - halfHeight + overhangY)
	const safeMinX = paddingX + halfWidth - overhangX
	const safeMaxX = width - paddingX - halfWidth + overhangX
	const safeMinY = paddingY + halfHeight - overhangY
	const safeMaxY = height - paddingY - halfHeight + overhangY
	const useSafeX = safeMinX <= safeMaxX
	const useSafeY = safeMinY <= safeMaxY

	return {
		minX: useSafeX ? safeMinX : hardMinX,
		maxX: useSafeX ? safeMaxX : hardMaxX,
		minY: useSafeY ? safeMinY : hardMinY,
		maxY: useSafeY ? safeMaxY : hardMaxY,
	}
}

const getCandidate = (
	item,
	width,
	height,
	paddingRatio,
	overhangRatio,
	rand,
	options = {},
) => {
	const bounds = getPlacementBounds(
		item,
		width,
		height,
		paddingRatio,
		overhangRatio,
	)
	const rangeX = Math.max(bounds.maxX - bounds.minX, 0)
	const rangeY = Math.max(bounds.maxY - bounds.minY, 0)
	let xRatio = rand()

	if (options.avoidCenterX) {
		const centerChance = options.centerXChance ?? 0.08
		const xMode =
			options.xMode ?? (rand() < centerChance ? 'center' : 'edge')

		if (xMode === 'center') {
			const centerSpan = options.centerXSpan ?? 0.22
			xRatio = 0.5 + (rand() - 0.5) * centerSpan
		} else {
			const edgeSpan = options.edgeXSpan ?? 0.38
			xRatio = rand() < 0.5 ? rand() * edgeSpan : 1 - rand() * edgeSpan
		}
	}

	return {
		x: bounds.minX + rangeX * clamp(xRatio, 0, 1),
		y: bounds.minY + rangeY * rand(),
	}
}

const getPairGap = (
	candidate,
	item,
	placedItem,
	width,
	height,
	options = {},
) => {
	const dx = Math.abs(candidate.x - placedItem.x)
	const dy = Math.abs(candidate.y - placedItem.y)
	const minDx = (item.width + placedItem.width) / 2
	const minDy = (item.height + placedItem.height) / 2
	const gapX = (dx - minDx) / width
	const gapY = (dy - minDy) / height
	const overlapX = Math.max(0, -gapX)
	const overlapY = Math.max(0, -gapY)
	const positiveGapX = Math.max(gapX, 0)
	const positiveGapY = Math.max(gapY, 0)

	if (overlapX > 0 && overlapY > 0) {
		return -Math.hypot(overlapX, overlapY)
	}

	if (options.balanceAxis) {
		return Math.hypot(positiveGapX * 1.4, positiveGapY * 0.5)
	}

	return Math.hypot(positiveGapX, positiveGapY)
}

const getEdgeGap = (
	candidate,
	item,
	width,
	height,
	paddingRatio,
	overhangRatio = 0,
) => {
	const paddingX = width * paddingRatio
	const paddingY = height * paddingRatio
	const overhangX = item.width * overhangRatio
	const overhangY = item.height * overhangRatio
	const left = candidate.x - item.width / 2 - paddingX + overhangX
	const right = width - paddingX + overhangX - (candidate.x + item.width / 2)
	const top = candidate.y - item.height / 2 - paddingY + overhangY
	const bottom =
		height - paddingY + overhangY - (candidate.y + item.height / 2)

	return Math.min(left / width, right / width, top / height, bottom / height)
}

const spreadItems = (itemsElem, itemElems, options = {}) => {
	const { width, height } = itemsElem.getBoundingClientRect()
	if (!width || !height) {
		return
	}

	const phone = isPhone()

	const items = itemElems.map((item, index) => {
		let { width, height } = item.getBoundingClientRect()

		if (!phone) {
			const ratio = randomInRange(0.5, 0.75)
			width *= ratio
			height *= ratio
		}

		return {
			index,
			item,
			width,
			height,
			area: width * height,
		}
	})

	const paddingRatio = phone ? 0 : (options.paddingRatio ?? 0.025)
	const overhangRatio = phone ? (options.overhangRatio ?? 0.125) : 0
	const centerXChance = phone ? (options.centerXChance ?? 0.3) : 0
	const centerXPenalty = phone ? (1 - centerXChance) * 0.18 : 0
	const sampleCount = options.sampleCount ?? 96
	const mulberry32 = seed => () => {
		let t = (seed += 0x6d2b79f5)
		t = Math.imul(t ^ (t >>> 15), t | 1)
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296
	}
	const rand =
		options.seed !== undefined ? mulberry32(options.seed) : Math.random

	const sortedItems = [...items].sort((a, b) => b.area - a.area)
	const placedItems = []

	sortedItems.forEach(item => {
		let bestCandidate = null
		let bestScore = -Infinity
		const xMode = phone && rand() < centerXChance ? 'center' : 'edge'

		for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex++) {
			const candidate = getCandidate(
				item,
				width,
				height,
				paddingRatio,
				overhangRatio,
				rand,
				{
					avoidCenterX: phone,
					xMode,
					centerXChance,
					centerXSpan: 0.18,
					edgeXSpan: 0.4,
				},
			)
			const pairGap = placedItems.length
				? Math.min(
						...placedItems.map(placedItem =>
							getPairGap(
								candidate,
								item,
								placedItem,
								width,
								height,
								{ balanceAxis: phone },
							),
						),
					)
				: 0
			const edgeGap = getEdgeGap(
				candidate,
				item,
				width,
				height,
				paddingRatio,
				overhangRatio,
			)
			const score =
				pairGap * 0.85 +
				edgeGap * 0.15 +
				(phone
					? (1 - Math.abs(candidate.x / width - 0.5) * 2) ** 2 *
						-centerXPenalty
					: 0)

			if (score > bestScore) {
				bestScore = score
				bestCandidate = candidate
			}
		}

		placedItems.push({ ...item, ...bestCandidate })
	})

	return items.map(item => {
		const placedItem = placedItems.find(
			placed => placed.index === item.index,
		)
		return [
			clamp(placedItem.x / width, 0, 1),
			clamp(placedItem.y / height, 0, 1),
		]
	})
}

const waitForImages = async items =>
	Promise.all(
		items
			.map(item => item.querySelector('img'))
			.filter(Boolean)
			.map(img => {
				if (img.complete) {
					return Promise.resolve()
				}

				return new Promise(resolve => {
					const done = () => {
						img.removeEventListener('load', done)
						img.removeEventListener('error', done)
						resolve()
					}

					img.addEventListener('load', done, { once: true })
					img.addEventListener('error', done, { once: true })
				})
			}),
	)

const installMouseParallax = (stageElem, itemsElem, itemElems) => {
	const parallaxQuery = window.matchMedia(
		'(pointer: fine) and (hover: hover)',
	)
	const itemStates = new Map()
	let pointerFrameId = null
	let scrollFrameId = null
	let animationFrameId = null
	let phoneLerpFrameId = null
	let virtualPointerFrameId = null
	let idleLoopTimeoutId = null
	let idleLoopEnabled = false
	let lastRealPointerMoveAt = performance.now()
	let lastPointerPosition = {
		ratio: [0.5, 1],
		source: 'virtual',
	}
	let lastRealPointerCanvasPosition = null
	let lastRealPointerDirection = null
	let baseDirectionX = 0
	let baseDirectionY = 0
	const idleLoopDelay = 100
	const virtualPointerSpeed = 140 // px/sec

	const resetParallax = () => {
		const now = performance.now()
		baseDirectionX = 0
		baseDirectionY = 0

		itemElems.forEach(item => {
			const state = itemStates.get(item)
			if (!state) {
				return
			}

			state.fromX = state.currentX
			state.fromY = state.currentY
			state.targetX = 0
			state.targetY = 0
			state.startAt = now
			state.duration = state.isHovered ? 1000 : state.duration
			state.ease = easeOutCubic
			state.hoverFromX = state.hoverCurrentX
			state.hoverFromY = state.hoverCurrentY
			state.hoverTargetX = 0
			state.hoverTargetY = 0
			state.hoverStartAt = now
			state.hoverDuration = 1000
			state.hoverEase = easeOutCubic
		})

		startAnimation()
	}

	const renderState = (item, state) => {
		item.style.setProperty(
			'--parallax-x',
			`${state.currentX + state.hoverCurrentX}px`,
		)
		item.style.setProperty(
			'--parallax-y',
			`${state.currentY + state.hoverCurrentY}px`,
		)
	}

	const stepAnimation = now => {
		let hasActiveAnimation = false

		itemElems.forEach(item => {
			const state = itemStates.get(item)
			if (!state) {
				return
			}

			const elapsed = now - state.startAt
			const progress = clamp(elapsed / state.duration, 0, 1)
			const easedProgress = state.ease(progress)
			const hoverElapsed = now - state.hoverStartAt
			const hoverProgress = clamp(
				hoverElapsed / state.hoverDuration,
				0,
				1,
			)
			const easedHoverProgress = state.hoverEase(hoverProgress)

			state.currentX =
				state.fromX + (state.targetX - state.fromX) * easedProgress
			state.currentY =
				state.fromY + (state.targetY - state.fromY) * easedProgress
			state.hoverCurrentX =
				state.hoverFromX +
				(state.hoverTargetX - state.hoverFromX) * easedHoverProgress
			state.hoverCurrentY =
				state.hoverFromY +
				(state.hoverTargetY - state.hoverFromY) * easedHoverProgress
			renderState(item, state)

			if (progress < 1 || hoverProgress < 1) {
				hasActiveAnimation = true
			}
		})

		if (hasActiveAnimation) {
			animationFrameId = requestAnimationFrame(stepAnimation)
			return
		}

		animationFrameId = null
	}

	const startAnimation = () => {
		if (animationFrameId) {
			return
		}

		animationFrameId = requestAnimationFrame(stepAnimation)
	}

	const renderPhoneLerp = () => {
		let hasMotion = false

		itemElems.forEach(item => {
			const state = itemStates.get(item)
			if (!state) {
				return
			}

			state.currentX += (state.targetX - state.currentX) * 0.12
			state.currentY += (state.targetY - state.currentY) * 0.12

			if (Math.abs(state.targetX - state.currentX) < 0.1) {
				state.currentX = state.targetX
			} else {
				hasMotion = true
			}

			if (Math.abs(state.targetY - state.currentY) < 0.1) {
				state.currentY = state.targetY
			} else {
				hasMotion = true
			}

			renderState(item, state)
		})

		if (hasMotion) {
			phoneLerpFrameId = requestAnimationFrame(renderPhoneLerp)
			return
		}

		phoneLerpFrameId = null
	}

	const startPhoneLerp = () => {
		if (phoneLerpFrameId) {
			return
		}

		phoneLerpFrameId = requestAnimationFrame(renderPhoneLerp)
	}

	const applyPhoneParallax = () => {
		if (!isPhone()) {
			return
		}

		const stageRect = stageElem.getBoundingClientRect()
		const stableViewportHeight = getStableViewportHeight()
		const visualViewport = window.visualViewport
		const viewportLeft = window.scrollX + (visualViewport?.offsetLeft ?? 0)
		const viewportTop = window.scrollY + (visualViewport?.offsetTop ?? 0)
		const viewportWidth = stageRect.width || window.innerWidth
		const viewportCenterX = viewportLeft + viewportWidth / 2
		const viewportCenterY = viewportTop + stableViewportHeight / 2
		const maxDistance =
			Math.hypot(viewportWidth / 2, stableViewportHeight / 2) || 1
		const maxX = isSmallPhone() ? 14 : 18
		const maxY = isSmallPhone() ? 24 : 30

		itemElems.forEach(item => {
			const state = itemStates.get(item)

			if (!state) {
				return
			}

			const itemRect = item.getBoundingClientRect()
			const centerX = viewportLeft + itemRect.left + itemRect.width / 2
			const centerY = viewportTop + itemRect.top + itemRect.height / 2
			const deltaX = centerX - viewportCenterX
			const deltaY = centerY - viewportCenterY
			const distanceRatio = clamp(
				Math.hypot(deltaX, deltaY) / maxDistance,
				0,
				1,
			)

			state.targetX =
				(-deltaX / (viewportWidth / 2 || 1)) * maxX * distanceRatio
			state.targetY =
				(-deltaY / (stableViewportHeight / 2 || 1)) *
				maxY *
				distanceRatio
		})

		startPhoneLerp()
	}

	const applyParallax = (event, options = {}) => {
		if (isPhone()) {
			applyPhoneParallax()
			return
		}

		if (
			(!options.forcePointer && !parallaxQuery.matches) ||
			event.pointerType === 'touch'
		) {
			resetParallax()
			return
		}

		const stageRect = stageElem.getBoundingClientRect()
		const itemsRect = itemsElem.getBoundingClientRect()
		const pointerX =
			event.clientX -
			stageRect.left +
			stageElem.scrollLeft -
			itemsElem.offsetLeft
		const pointerY =
			event.clientY -
			stageRect.top +
			stageElem.scrollTop -
			itemsElem.offsetTop
		const pointerSource = options.pointerSource || 'real'
		const viewportPointerX = event.clientX - stageRect.left
		const viewportPointerY = event.clientY - stageRect.top
		const viewportCenterX = stageRect.width / 2
		const viewportCenterY = stageRect.height / 2

		if (pointerSource === 'real') {
			if (lastRealPointerCanvasPosition) {
				const deltaX = pointerX - lastRealPointerCanvasPosition.x
				const deltaY = pointerY - lastRealPointerCanvasPosition.y
				const distance = Math.hypot(deltaX, deltaY)

				if (distance > 0.5) {
					lastRealPointerDirection = {
						x: deltaX / distance,
						y: deltaY / distance,
					}
				}
			}

			lastRealPointerCanvasPosition = {
				x: pointerX,
				y: pointerY,
			}
		}

		lastPointerPosition = {
			ratio: [
				clamp(viewportPointerX / (stageRect.width || 1), 0, 1),
				clamp(viewportPointerY / (stageRect.height || 1), 0, 1),
			],
			source: pointerSource,
		}
		const directionX = viewportCenterX - viewportPointerX
		const directionY = viewportCenterY - viewportPointerY
		const directionDistance = Math.hypot(directionX, directionY)
		const directionDivisor = directionDistance || 1
		const targetDirectionX =
			directionDistance > 1
				? directionX / directionDivisor
				: baseDirectionX
		const targetDirectionY =
			directionDistance > 1
				? directionY / directionDivisor
				: baseDirectionY
		const directionLerp = 0.16
		baseDirectionX += (targetDirectionX - baseDirectionX) * directionLerp
		baseDirectionY += (targetDirectionY - baseDirectionY) * directionLerp
		const unitX = baseDirectionX
		const unitY = baseDirectionY
		const maxDistance = Math.hypot(stageRect.width, stageRect.height) || 1
		const hoveredItem = itemElems.find(item => item.matches(':hover'))
		const itemMetrics = itemElems.map(item => {
			const metricPointer =
				item === hoveredItem && lastRealPointerCanvasPosition
					? lastRealPointerCanvasPosition
					: { x: pointerX, y: pointerY }
			const centerX = Number(item.dataset.homeX) * itemsRect.width
			const centerY = Number(item.dataset.homeY) * itemsRect.height
			const itemDistance = Math.hypot(
				metricPointer.x - centerX,
				metricPointer.y - centerY,
			)
			const itemDistanceRatio = clamp(itemDistance / maxDistance, 0, 1)
			const influence = 0.5 + (1 - itemDistanceRatio) * 0.5

			return {
				item,
				centerX,
				centerY,
				itemDistanceRatio,
				influence,
			}
		})
		const averageInfluence =
			itemMetrics.reduce((sum, metric) => sum + metric.influence, 0) /
				itemMetrics.length || 1
		const targetAverageInfluence = 0.75
		const influenceScale = targetAverageInfluence / averageInfluence
		const now = performance.now()

		itemMetrics.forEach(
			({ item, centerX, centerY, itemDistanceRatio, influence }) => {
				const state = itemStates.get(item)
				if (!state) {
					return
				}

				const isHovered = item === hoveredItem

				if (pointerSource === 'virtual' && isHovered) {
					return
				}

				const itemInfluence = clamp(
					influence * influenceScale,
					0.45,
					1.05,
				)
				const nonHoverMagnitude = 110 * itemInfluence
				const nonHoverDuration = 800 + 1200 * itemDistanceRatio
				const baseTargetX = unitX * nonHoverMagnitude
				const baseTargetY = unitY * nonHoverMagnitude
				const baseCenterX = centerX + baseTargetX
				const baseCenterY = centerY + baseTargetY
				const hoverPointer =
					isHovered && lastRealPointerCanvasPosition
						? lastRealPointerCanvasPosition
						: { x: pointerX, y: pointerY }
				const hoverDeltaX = hoverPointer.x - baseCenterX
				const hoverDeltaY = hoverPointer.y - baseCenterY
				const hoverDistance = Math.hypot(hoverDeltaX, hoverDeltaY)
				const hoverDivisor = hoverDistance || 1
				const hoverMagnitude = Math.min(
					hoverDistance,
					nonHoverMagnitude * 0.7,
				)
				const hoverDuration = nonHoverDuration * 1.45
				const rawHoverTargetX = isHovered
					? (hoverDeltaX / hoverDivisor) * hoverMagnitude
					: 0
				const rawHoverTargetY = isHovered
					? (hoverDeltaY / hoverDivisor) * hoverMagnitude
					: 0
				const hoverTargetLerp = isHovered ? 0.18 : 1
				const hoverTargetX =
					state.hoverTargetX +
					(rawHoverTargetX - state.hoverTargetX) * hoverTargetLerp
				const hoverTargetY =
					state.hoverTargetY +
					(rawHoverTargetY - state.hoverTargetY) * hoverTargetLerp

				state.fromX = state.currentX
				state.fromY = state.currentY
				state.targetX = baseTargetX
				state.targetY = baseTargetY
				state.startAt = now
				state.isHovered = isHovered
				state.duration = nonHoverDuration
				state.ease = easeOutCubic
				state.hoverFromX = state.hoverCurrentX
				state.hoverFromY = state.hoverCurrentY
				state.hoverTargetX = hoverTargetX
				state.hoverTargetY = hoverTargetY
				state.hoverStartAt = now
				state.hoverDuration = isHovered ? hoverDuration : 1000
				state.hoverEase = easeOutCubic
			},
		)

		startAnimation()
	}

	const getStagePoint = (xRatio, yRatio) => {
		const stageRect = stageElem.getBoundingClientRect()

		return {
			clientX: stageRect.left + stageRect.width * xRatio,
			clientY: stageRect.top + stageRect.height * yRatio,
			pointerType: 'mouse',
		}
	}

	const stopVirtualPointer = () => {
		if (virtualPointerFrameId) {
			cancelAnimationFrame(virtualPointerFrameId)
			virtualPointerFrameId = null
		}
	}

	const getLoopIntersectionFromPointerDirection = (
		pointRatio,
		pathRatios,
	) => {
		if (!lastRealPointerDirection) {
			return null
		}

		const stageRect = stageElem.getBoundingClientRect()
		const stageWidth = stageRect.width || window.innerWidth || 1
		const stageHeight = stageRect.height || window.innerHeight || 1
		const pointX = pointRatio[0] * stageWidth
		const pointY = pointRatio[1] * stageHeight
		const directionLength = Math.hypot(
			lastRealPointerDirection.x,
			lastRealPointerDirection.y,
		)

		if (!directionLength) {
			return null
		}

		const directionX = lastRealPointerDirection.x / directionLength
		const directionY = lastRealPointerDirection.y / directionLength
		const cross = (aX, aY, bX, bY) => aX * bY - aY * bX
		let intersection = null

		pathRatios.forEach((fromPoint, segmentIndex) => {
			const toPoint = pathRatios[(segmentIndex + 1) % pathRatios.length]
			const fromX = fromPoint[0] * stageWidth
			const fromY = fromPoint[1] * stageHeight
			const deltaX = (toPoint[0] - fromPoint[0]) * stageWidth
			const deltaY = (toPoint[1] - fromPoint[1]) * stageHeight
			const denominator = cross(directionX, directionY, deltaX, deltaY)

			if (Math.abs(denominator) < 0.0001) {
				return
			}

			const offsetX = fromX - pointX
			const offsetY = fromY - pointY
			const rayProgress =
				cross(offsetX, offsetY, deltaX, deltaY) / denominator
			const segmentProgress =
				cross(offsetX, offsetY, directionX, directionY) / denominator

			if (
				rayProgress < -0.1 ||
				segmentProgress < -0.0001 ||
				segmentProgress > 1.0001
			) {
				return
			}

			if (intersection && rayProgress >= intersection.rayProgress) {
				return
			}

			const progress = clamp(segmentProgress, 0, 1)

			intersection = {
				point: [
					fromPoint[0] + (toPoint[0] - fromPoint[0]) * progress,
					fromPoint[1] + (toPoint[1] - fromPoint[1]) * progress,
				],
				progress,
				segmentIndex,
				rayProgress,
			}
		})

		return intersection
	}

	const rotateLoopPoints = (pathRatios, startIndex) =>
		Array.from(
			{ length: pathRatios.length },
			(_, index) => pathRatios[(startIndex + index) % pathRatios.length],
		)

	const getLoopDirection = (pathRatios, intersection) => {
		if (!lastRealPointerDirection) {
			return 1
		}

		const stageRect = stageElem.getBoundingClientRect()
		const stageWidth = stageRect.width || window.innerWidth || 1
		const stageHeight = stageRect.height || window.innerHeight || 1
		const pointCount = pathRatios.length
		const forwardTarget =
			intersection.progress >= 0.9999
				? pathRatios[(intersection.segmentIndex + 2) % pointCount]
				: pathRatios[(intersection.segmentIndex + 1) % pointCount]
		const reverseTarget =
			intersection.progress <= 0.0001
				? pathRatios[
						(intersection.segmentIndex - 1 + pointCount) %
							pointCount
					]
				: pathRatios[intersection.segmentIndex]
		const getDot = targetPoint => {
			const deltaX = (targetPoint[0] - intersection.point[0]) * stageWidth
			const deltaY =
				(targetPoint[1] - intersection.point[1]) * stageHeight
			const distance = Math.hypot(deltaX, deltaY)

			if (!distance) {
				return -Infinity
			}

			return (
				lastRealPointerDirection.x * (deltaX / distance) +
				lastRealPointerDirection.y * (deltaY / distance)
			)
		}
		const forwardDot = getDot(forwardTarget)
		const reverseDot = getDot(reverseTarget)

		return forwardDot >= reverseDot ? 1 : -1
	}

	const getDirectedLoopPoints = (pathRatios, intersection, direction) => {
		const pointCount = pathRatios.length

		if (direction === -1) {
			if (intersection.progress <= 0.0001) {
				return Array.from(
					{ length: pointCount },
					(_, index) =>
						pathRatios[
							(intersection.segmentIndex - index + pointCount) %
								pointCount
						],
				)
			}

			if (intersection.progress >= 0.9999) {
				const intersectionIndex =
					(intersection.segmentIndex + 1) % pointCount

				return Array.from(
					{ length: pointCount },
					(_, index) =>
						pathRatios[
							(intersectionIndex - index + pointCount) %
								pointCount
						],
				)
			}

			return [
				intersection.point,
				...Array.from(
					{ length: pointCount },
					(_, index) =>
						pathRatios[
							(intersection.segmentIndex - index + pointCount) %
								pointCount
						],
				),
			]
		}

		if (intersection.progress <= 0.0001) {
			return rotateLoopPoints(pathRatios, intersection.segmentIndex)
		}

		if (intersection.progress >= 0.9999) {
			return rotateLoopPoints(
				pathRatios,
				(intersection.segmentIndex + 1) % pointCount,
			)
		}

		return [
			intersection.point,
			...Array.from(
				{ length: pointCount },
				(_, index) =>
					pathRatios[
						(intersection.segmentIndex + 1 + index) % pointCount
					],
			),
		]
	}

	const getLoopPathFromPointerDirection = (pathRatios, pointRatio) => {
		const intersection = getLoopIntersectionFromPointerDirection(
			pointRatio,
			pathRatios,
		)

		if (!intersection) {
			return rotateLoopPoints(pathRatios, 0)
		}

		const direction = getLoopDirection(pathRatios, intersection)

		return getDirectedLoopPoints(pathRatios, intersection, direction)
	}

	const playVirtualPointerPath = (
		pointRatios,
		{
			loop = false,
			loopStartIndex = 0,
			speed = virtualPointerSpeed,
			onComplete,
		} = {},
	) => {
		if (isPhone() || pointRatios.length < 2) {
			onComplete?.()
			return
		}

		stopVirtualPointer()

		const startedAt = performance.now()
		const stageRect = stageElem.getBoundingClientRect()
		const stageWidth = stageRect.width || window.innerWidth || 1
		const stageHeight = stageRect.height || window.innerHeight || 1
		const pointerSpeed = Math.max(speed, 1)
		const createSegment = (fromIndex, toIndex) => {
			const fromPoint = pointRatios[fromIndex] || pointRatios[0]
			const toPoint = pointRatios[toIndex] || fromPoint
			const distance = Math.hypot(
				(toPoint[0] - fromPoint[0]) * stageWidth,
				(toPoint[1] - fromPoint[1]) * stageHeight,
			)

			return {
				fromPoint,
				toPoint,
				duration: Math.max((distance / pointerSpeed) * 1000, 1),
			}
		}
		const clampedLoopStartIndex = loop
			? clamp(loopStartIndex, 0, pointRatios.length - 1)
			: 0
		const introSegments =
			loop && clampedLoopStartIndex > 0
				? Array.from({ length: clampedLoopStartIndex }, (_, index) =>
						createSegment(index, index + 1),
					)
				: []
		const pathSegmentCount = loop
			? pointRatios.length - clampedLoopStartIndex
			: pointRatios.length - 1
		const pathSegments = Array.from(
			{ length: pathSegmentCount },
			(_, index) => {
				const fromIndex = clampedLoopStartIndex + index
				const toIndex =
					loop && fromIndex === pointRatios.length - 1
						? clampedLoopStartIndex
						: fromIndex + 1

				return createSegment(fromIndex, toIndex)
			},
		)
		const introDuration = introSegments.reduce(
			(total, segment) => total + segment.duration,
			0,
		)
		const pathDuration = pathSegments.reduce(
			(total, segment) => total + segment.duration,
			0,
		)
		const duration = introDuration + pathDuration

		const getActiveSegment = (segments, elapsed) => {
			let elapsedBeforeSegment = 0
			let activeSegment = segments[0]

			for (const segment of segments) {
				if (elapsed <= elapsedBeforeSegment + segment.duration) {
					activeSegment = segment
					break
				}

				elapsedBeforeSegment += segment.duration
			}

			return {
				elapsedBeforeSegment,
				segment: activeSegment,
			}
		}

		const step = now => {
			const elapsed = Math.max(now - startedAt, 0)
			const isIntro = loop && elapsed < introDuration
			const pathElapsed = isIntro
				? elapsed
				: loop
					? (elapsed - introDuration) % pathDuration
					: clamp(elapsed, 0, duration)
			const activeSegments = isIntro ? introSegments : pathSegments
			const { elapsedBeforeSegment, segment: activeSegment } =
				getActiveSegment(activeSegments, pathElapsed)

			const segmentProgress = clamp(
				(pathElapsed - elapsedBeforeSegment) / activeSegment.duration,
				0,
				1,
			)
			const { fromPoint, toPoint } = activeSegment
			const xRatio =
				fromPoint[0] + (toPoint[0] - fromPoint[0]) * segmentProgress
			const yRatio =
				fromPoint[1] + (toPoint[1] - fromPoint[1]) * segmentProgress

			applyParallax(getStagePoint(xRatio, yRatio), {
				forcePointer: true,
				pointerSource: 'virtual',
			})

			if (!loop && elapsed >= duration) {
				virtualPointerFrameId = null
				onComplete?.()
				return
			}

			virtualPointerFrameId = requestAnimationFrame(step)
		}

		virtualPointerFrameId = requestAnimationFrame(step)
	}

	const startIdleLoop = ({ force = false } = {}) => {
		if (!idleLoopEnabled || isPhone()) {
			return
		}

		const idleFor = performance.now() - lastRealPointerMoveAt

		if (!force && idleFor < idleLoopDelay) {
			return
		}

		const idleLoopPoints = [
			[0, 0],
			[1, 0],
			[1, 1],
			[0, 1],
		]
		const startPointRatio =
			lastPointerPosition.source === 'real' ||
			lastPointerPosition.source === 'virtual'
				? lastPointerPosition.ratio
				: idleLoopPoints[0]
		const loopPoints = getLoopPathFromPointerDirection(
			idleLoopPoints,
			startPointRatio,
		)

		if (force) {
			playVirtualPointerPath(loopPoints, {
				loop: true,
			})
		} else {
			playVirtualPointerPath([startPointRatio, ...loopPoints], {
				loop: true,
				loopStartIndex: 1,
			})
		}
	}

	const startIdleLoopImmediately = () => {
		if (isPhone()) {
			return
		}

		idleLoopEnabled = true

		if (idleLoopTimeoutId) {
			clearTimeout(idleLoopTimeoutId)
			idleLoopTimeoutId = null
		}

		startIdleLoop({ force: true })
	}

	const scheduleIdleLoop = () => {
		if (!idleLoopEnabled) {
			return
		}

		if (idleLoopTimeoutId) {
			clearTimeout(idleLoopTimeoutId)
		}

		idleLoopTimeoutId = setTimeout(() => {
			idleLoopTimeoutId = null
			startIdleLoop()
		}, idleLoopDelay)
	}

	const noteRealPointerMove = () => {
		idleLoopEnabled = true
		lastRealPointerMoveAt = performance.now()
		stopVirtualPointer()
		scheduleIdleLoop()
	}

	const onPointerMove = event => {
		if (isPhone()) {
			return
		}

		noteRealPointerMove()

		if (pointerFrameId) {
			cancelAnimationFrame(pointerFrameId)
		}

		pointerFrameId = requestAnimationFrame(() => {
			applyParallax(event)
			pointerFrameId = null
		})
	}

	const onPointerLeave = () => {
		if (pointerFrameId) {
			cancelAnimationFrame(pointerFrameId)
			pointerFrameId = null
		}

		if (phoneLerpFrameId) {
			cancelAnimationFrame(phoneLerpFrameId)
			phoneLerpFrameId = null
		}
	}

	const onScroll = () => {
		if (!isPhone()) {
			return
		}

		if (scrollFrameId) {
			cancelAnimationFrame(scrollFrameId)
		}

		scrollFrameId = requestAnimationFrame(() => {
			applyPhoneParallax()
			scrollFrameId = null
		})
	}

	itemElems.forEach(item => {
		itemStates.set(item, {
			currentX: 0,
			currentY: 0,
			fromX: 0,
			fromY: 0,
			targetX: 0,
			targetY: 0,
			startAt: performance.now(),
			duration: 1500,
			ease: easeOutCubic,
			hoverCurrentX: 0,
			hoverCurrentY: 0,
			hoverFromX: 0,
			hoverFromY: 0,
			hoverTargetX: 0,
			hoverTargetY: 0,
			hoverStartAt: performance.now(),
			hoverDuration: 1000,
			hoverEase: easeOutCubic,
			isHovered: false,
		})
		renderState(item, itemStates.get(item))
	})

	stageElem.addEventListener('pointermove', onPointerMove)
	stageElem.addEventListener('pointerleave', onPointerLeave)
	window.addEventListener('scroll', onScroll, { passive: true })
	parallaxQuery.addEventListener('change', () => {
		if (isPhone()) {
			applyPhoneParallax()
			return
		}

		stopVirtualPointer()
		resetParallax()
	})

	if (isPhone()) {
		applyPhoneParallax()
		setTimeout(onScroll, 100)
	} else {
		resetParallax()
	}

	return {
		update: () => {
			if (isPhone()) {
				applyPhoneParallax()
				return
			}

			resetParallax()
		},
		startIdleLoopImmediately,
	}
}

const installDesktopDragScroll = stageElem => {
	const dragScrollQuery = window.matchMedia(
		'(pointer: fine) and (hover: hover)',
	)
	let pointerId = null
	let startX = 0
	let startY = 0
	let startScrollLeft = 0
	let startScrollTop = 0
	let isDragging = false
	let suppressClick = false

	const resetDrag = () => {
		pointerId = null
		isDragging = false
		stageElem.classList.remove('-dragging')
	}

	const onPointerDown = event => {
		if (!dragScrollQuery.matches || event.pointerType === 'touch') {
			return
		}

		if (event.button !== 0) {
			return
		}

		const interactiveElem = event.target.closest(
			'a, button, input, textarea, select, label',
		)
		if (interactiveElem) {
			return
		}

		pointerId = event.pointerId
		startX = event.clientX
		startY = event.clientY
		startScrollLeft = stageElem.scrollLeft
		startScrollTop = stageElem.scrollTop
		isDragging = false
		stageElem.setPointerCapture(pointerId)
	}

	const onPointerMove = event => {
		if (event.pointerId !== pointerId) {
			return
		}

		const deltaX = event.clientX - startX
		const deltaY = event.clientY - startY

		if (!isDragging && Math.hypot(deltaX, deltaY) > 6) {
			isDragging = true
			suppressClick = true
			stageElem.classList.add('-dragging')
		}

		if (!isDragging) {
			return
		}

		stageElem.scrollLeft = startScrollLeft - deltaX
		stageElem.scrollTop = startScrollTop - deltaY
	}

	const onPointerUp = event => {
		if (event.pointerId !== pointerId) {
			return
		}

		if (stageElem.hasPointerCapture(pointerId)) {
			stageElem.releasePointerCapture(pointerId)
		}

		resetDrag()
	}

	const onPointerCancel = event => {
		if (event.pointerId !== pointerId) {
			return
		}

		resetDrag()
	}

	const onClickCapture = event => {
		if (!suppressClick) {
			return
		}

		event.preventDefault()
		event.stopPropagation()
		suppressClick = false
	}

	const onDragStart = event => {
		if (dragScrollQuery.matches) {
			event.preventDefault()
		}
	}

	stageElem.addEventListener('pointerdown', onPointerDown)
	stageElem.addEventListener('pointermove', onPointerMove)
	stageElem.addEventListener('pointerup', onPointerUp)
	stageElem.addEventListener('pointercancel', onPointerCancel)
	stageElem.addEventListener('click', onClickCapture, true)
	stageElem.addEventListener('dragstart', onDragStart)
	dragScrollQuery.addEventListener('change', () => {
		resetDrag()
		suppressClick = false
	})
}

document.addEventListener('DOMContentLoaded', async () => {
	const loadingElem = document.querySelector('.home-loading')
	const stageElem = document.querySelector('.home-stage')
	const resizingMaskElem = stageElem?.querySelector('.resizing-mask')
	if (!loadingElem || !stageElem || !resizingMaskElem) {
		return
	}

	const itemsElem = stageElem.querySelector(':scope > .items')
	if (!itemsElem) {
		return
	}

	const itemElems = [...itemsElem.querySelectorAll(':scope > .item')]
	if (itemElems.length === 0) {
		return
	}
	const itemInfoElem = stageElem.querySelector(':scope > .item-info')
	const itemInfoLabelElem = itemInfoElem?.querySelector(':scope > .label')
	const itemInfoDescriptionElem =
		itemInfoElem?.querySelector(':scope > .info')

	itemElems.forEach(item => {
		item.querySelectorAll('p').forEach(p => {
			const span = document.createElement('span')

			while (p.firstChild) {
				span.appendChild(p.firstChild)
			}

			p.appendChild(span)
		})
	})

	const getItemInfo = item => ({
		label: item.querySelector('.label')?.innerHTML || '',
		info: item.querySelector('.info')?.innerHTML || '',
	})

	const setItemInfo = item => {
		if (!itemInfoElem || !itemInfoLabelElem || !itemInfoDescriptionElem) {
			return
		}

		const { label, info } = getItemInfo(item)
		itemInfoLabelElem.innerHTML = label
		itemInfoDescriptionElem.innerHTML = info

		itemInfoElem.classList.toggle('on', Boolean(label || info))
	}

	const hideItemInfo = () => {
		itemInfoElem?.classList.remove('on')
	}

	const installItemInfoEvents = () => {
		if (!itemInfoElem) {
			return
		}

		itemElems.forEach(item => {
			item.addEventListener('mouseenter', () => {
				if (isPhone()) {
					return
				}

				setItemInfo(item)
			})

			item.addEventListener('mouseleave', () => {
				if (isPhone()) {
					return
				}

				hideItemInfo()
			})

			item.addEventListener(
				'touchstart',
				() => {
					setItemInfo(item)
				},
				{ passive: true },
			)

			item.addEventListener('touchend', hideItemInfo)
			item.addEventListener('touchcancel', hideItemInfo)
		})

		document.addEventListener(
			'touchstart',
			event => {
				if (!event.target.closest?.('.item')) {
					hideItemInfo()
				}
			},
			{ passive: true },
		)
	}

	let parallaxControls = {
		startIdleLoopImmediately: () => {},
		update: () => {},
	}
	let isLoadingHidden = false

	const hideLoadingElem = () => {
		if (!loadingElem || isLoadingHidden) {
			return
		}

		if (isPhone()) {
			window.scrollTo({ top: 0 })
		}

		isLoadingHidden = true
		loadingElem.classList.add('-hided')
		resizingMaskElem.classList.add('-hided')
		parallaxControls.startIdleLoopImmediately()
		setTimeout(() => {
			destroyLoadingType()
			loadingElem.remove()
		}, 6000)

		document.body.classList.add('s_')
	}

	// home-loading
	const destroyLoadingType = initLoadingType(loadingElem)
	await waitForImages(itemElems)

	let lastLayoutViewportWidth =
		window.innerWidth || document.documentElement.clientWidth || 0

	const layoutItems = () => {
		lastLayoutViewportWidth =
			window.innerWidth || document.documentElement.clientWidth || 0
		const [minSize, maxSize] = isSmallPhone()
			? [160, 235]
			: isPhone()
				? [200, 295]
				: [210, 305]

		itemElems.forEach(item => {
			item.style.setProperty(
				'--size',
				`${randomInRange(minSize, maxSize)}px`,
			)
		})

		setItemsCanvasSize(stageElem, itemsElem, itemElems)

		// set position
		const positions = spreadItems(itemsElem, itemElems)
		if (!positions) {
			return
		}

		itemElems.forEach((item, index) => {
			const [x, y] = positions[index]

			item.dataset.homeX = x
			item.dataset.homeY = y
			item.style.setProperty('--x', `${x * 100}%`)
			item.style.setProperty('--y', `${y * 100}%`)
			item.classList.add('-installed')
		})

		scrollStageToCenter(stageElem, itemsElem)
		parallaxControls.update()
	}

	let resizeFrame = null
	let resizeTimeout = null
	const onResize = () => {
		if (!isPhone()) {
			resizingMaskElem.classList.remove('-hided')
		}

		if (resizeFrame) {
			cancelAnimationFrame(resizeFrame)
		}

		resizeFrame = requestAnimationFrame(() => {
			const viewportWidth =
				window.innerWidth || document.documentElement.clientWidth || 0
			if (
				isPhone() &&
				Math.abs(viewportWidth - lastLayoutViewportWidth) < 1
			) {
				parallaxControls.update()
				resizeFrame = null
				return
			}

			layoutItems()
			resizeFrame = null

			if (!isPhone()) {
				if (resizeTimeout) {
					clearTimeout(resizeTimeout)
				}

				resizeTimeout = setTimeout(() => {
					resizingMaskElem.classList.add('-hided')
				}, 300)
			}
		})
	}

	layoutItems()
	parallaxControls = installMouseParallax(stageElem, itemsElem, itemElems)
	parallaxControls.update()
	loadingElem.addEventListener('click', hideLoadingElem)
	setTimeout(hideLoadingElem, 2500)
	installItemInfoEvents()
	installDesktopDragScroll(stageElem)
	window.addEventListener('resize', onResize)
})
